import { LocalTrack, LocalVideoTrack, RemoteTrack } from "livekit-client";
import { reactive, ref, shallowReactive, shallowRef } from "vue";
import { authState } from "./auth";
import { fetcher } from "../api/fetcher";
import { roomState } from "./room";
import { ws_instance } from "../websocket/ws-instance";
import { activeRoomState, restoreMode, setStreamMode } from "./active_room";
import { getLivekitRoomInstance } from "./media/livekit";

//TODO: Переписать на новое управление треками states/media/livekit.ts

export const selfScreenShare = ref(false);

const screenTracks = shallowReactive<{
  [key: string]: LocalTrack | RemoteTrack | null;
}>({});
const sharedByLivekit = reactive([]) as string[];
const sharedByApi = ref<string | null>(null);

export const localScreenTrack = shallowRef<LocalVideoTrack | null>(null);

export function newScreenTrackByLivekit(track: RemoteTrack, user: string) {
  if (sharedByLivekit.includes(user)) {
    const idx = sharedByLivekit.findIndex((item) => item === user);
    sharedByLivekit.splice(idx, 1);
  }
  sharedByLivekit.push(user);
  screenTracks[user] = track;
  track.on("ended", () => {
    removeScreenTrackByLivekit(user);
  });
  changeModeIfNeeded();
}

export function removeScreenTrackByLivekit(user: string) {
  if (sharedByLivekit.includes(user)) {
    const idx = sharedByLivekit.findIndex((item) => item === user);
    sharedByLivekit.splice(idx, 1);
  }
  screenTracks[user] = null;
  changeModeIfNeeded();
}

export function newSharedByApi(user: string) {
  sharedByApi.value = user;
  if (user !== authState.id) {
    selfScreenShare.value = false;
    localScreenTrack.value?.detach();
    localScreenTrack.value?.stop();
    localScreenTrack.value = null;
    const roomInstance = getLivekitRoomInstance();
    if (roomInstance && roomInstance.localParticipant.isScreenShareEnabled) {
      roomInstance.localParticipant.setScreenShareEnabled(false);
    }
  }
  changeModeIfNeeded();
}

export function removeSharedByApi(user: string) {
  if (sharedByApi.value === user) {
    sharedByApi.value = null;
  }
  if (user === authState.id) {
    selfScreenShare.value = false;
    localScreenTrack.value?.detach();
    localScreenTrack.value?.stop();
    localScreenTrack.value = null;
    const roomInstance = getLivekitRoomInstance();
    if (roomInstance && roomInstance.localParticipant.isScreenShareEnabled) {
      roomInstance.localParticipant.setScreenShareEnabled(false);
    }
  }
  changeModeIfNeeded();
}

export function clearSharedByApi() {
  sharedByApi.value = null;
  selfScreenShare.value = false;
  localScreenTrack.value?.detach();
  localScreenTrack.value?.stop();
  localScreenTrack.value = null;
  const roomInstance = getLivekitRoomInstance();
  if (roomInstance && roomInstance.localParticipant.isScreenShareEnabled) {
    roomInstance.localParticipant.setScreenShareEnabled(false);
  }
  changeModeIfNeeded();
}

export async function startScreenShare() {
  const roomInstance = getLivekitRoomInstance();
  if (roomInstance) {
    await sendStartScreenShareEvent();
    selfScreenShare.value = true;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;

    const actualWidth = screenWidth * window.devicePixelRatio;
    const actualHeight = screenHeight * window.devicePixelRatio;

    try {
      const pub = await roomInstance.localParticipant.setScreenShareEnabled(
        true,
        {
          audio: true,
          resolution: {
            width: actualWidth,
            height: actualHeight,
          },
        },
        {
          simulcast: true,
          degradationPreference: "maintain-resolution",
        },
      );
      if (pub) {
        pub.videoTrack?.on("ended", stopScreenShare);
        localScreenTrack.value = pub.videoTrack || null;
      }
      selfScreenShare.value = true;
    } catch {
      stopScreenShare();
    }
  }
}

export async function stopScreenShare() {
  selfScreenShare.value = false;
  localScreenTrack.value = null;
  const roomInstance = getLivekitRoomInstance();
  if (roomInstance && roomInstance.localParticipant.isScreenShareEnabled) {
    roomInstance.localParticipant.setScreenShareEnabled(false);
  }
  await sendEndScreenShareEvent();
}

async function sendStartScreenShareEvent() {
  const res = await fetcher.post({
    path: "/active-room/start-screen-share",
    payload: {
      user_id: authState.id,
      room_id: roomState.id,
    },
  });
  return res;
}

async function sendEndScreenShareEvent() {
  try {
    return await fetcher.post({
      path: "/active-room/end-screen-share",
      payload: {
        user_id: authState.id,
        room_id: roomState.id,
      },
    });
  } catch (err) { }
}

export function subscribeScreenShareEvents() {
  ws_instance.addEventListener("start-screen-share", (userId: string) => {
    newSharedByApi(userId);
  });
  ws_instance.addEventListener("end-screen-share", (uid: string) => {
    if (authState.id === uid) {
      selfScreenShare.value = false;
      localScreenTrack.value?.detach();
      localScreenTrack.value?.stop();
      localScreenTrack.value = null;
      const roomInstance = getLivekitRoomInstance();
      if (roomInstance && roomInstance.localParticipant.isScreenShareEnabled) {
        roomInstance.localParticipant.setScreenShareEnabled(false);
      }
    }
    removeSharedByApi(uid);
  });
}

export async function stopAdminScreenShare(uid: string) {
  await fetcher.delete({
    path: `/active-room/end-screen-share/${uid}`,
  });
}

function changeModeIfNeeded() {
  if (
    activeRoomState.mode === "stream" &&
    (sharedByApi.value === null || sharedByApi.value === authState.id)
  ) {
    restoreMode();
  } else if (activeRoomState.mode !== "stream" && existSharingNotThisUser()) {
    setStreamMode();
  }
}

export function userIsScreenShared(user: string) {
  return sharedByApi.value === user;
}

export function existSharingNotThisUser() {
  return !!sharedByApi.value && sharedByApi.value !== authState.id;
}

export function isScreenSharing() {
  return !!sharedByApi.value;
}

export function getSharedUserIdIncludingThis() {
  return sharedByApi.value;
}

export function getShareNotThisUserTrack() {
  if (sharedByApi.value === null) return null;
  if (sharedByApi.value === authState.id) return null;
  const track = screenTracks[sharedByApi.value];
  return track || null;
}
